import { Box, Divider, Grid, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";

import { useTranslation } from "next-i18next";
import { MDXRemoteSerializeResult } from "next-mdx-remote";

import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import React from "react";

import I18nLink from "../../../../components/Atoms/I18nLink";

const InfoBar = dynamic(() => import("../../../../components/Molecules/InfoBar"));

export type PostPreviewProps = {
    slug?: string;
    path?: string;
    title?: string;
    publishedAt: string;
    content?: string;
    content_react?: MDXRemoteSerializeResult<Record<string, unknown>>;
    excerpt?: string;
    tags?: string[];
    readingTime?: number;
    type?: string;
};

export type Props = {
    post: PostPreviewProps;
};

const PostPreview = ({
    post: { title, publishedAt, slug, excerpt, type, readingTime, path }
}: Props) => {
    const { locale } = useRouter();

    const { t } = useTranslation(["common", "articles"]);

    const readTimeStr = t("articles:readingTime", { val: readingTime });
    const infoRun = t("articles:run");
    const infoTip = t("articles:tip");
    const formattedDate = t("common:dateFormatted", {
        val: new Date(publishedAt),
        formatParams: {
            val: { weekday: "long", year: "numeric", month: "long", day: "numeric" }
        }
    });
    return (
        <Grid
            component="li"
            item
            xs={12}
            md={10}
            lg={8}
            key={slug}
            sx={{ listStyle: "none", paddingLeft: "0 !important" }}
        >
            <Typography
                variant="h3"
                component="h4"
                color="primary"
                align="center"
                mb={2}
                sx={{
                    a: {
                        color: (theme) => theme.palette.primary.main
                    }
                }}
            >
                {path ? (
                    <I18nLink
                        href={locale !== "en" ? `/${locale}/${path}` : path}
                        locale={locale}
                    >
                        {title}
                    </I18nLink>
                ) : (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <>{title}</>
                )}
            </Typography>

            <Typography
                variant="h4"
                component="h5"
                align="center"
                mb={2}
                color={grey[700]}
            >
                {formattedDate}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 2
                }}
            >
                {readingTime ? (
                    <InfoBar
                        label={readTimeStr}
                        emoji="🕑"
                        color={grey[100]}
                        emojiAria="clock"
                    />
                ) : null}

                {type?.toLowerCase() === "run" ? (
                    <InfoBar
                        label={infoRun}
                        emoji="🏃"
                        color={grey[50]}
                        emojiAria="runner"
                        sx={{ mx: 2 }}
                    />
                ) : null}

                {type?.toLowerCase() === "tip" ? (
                    <InfoBar
                        label={infoTip}
                        emoji="🧠"
                        color={grey[50]}
                        emojiAria="brain"
                        sx={{ mx: 2 }}
                    />
                ) : null}
            </Box>

            <Typography
                component="div"
                dangerouslySetInnerHTML={{ __html: excerpt || "" }}
            />

            <Divider component="hr" sx={{ marginTop: 2, marginBottom: 4 }} light />
        </Grid>
    );
};

export default PostPreview;
